import { Info } from '@mui/icons-material';
import {
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { calculateRequestDetails, formatMoney } from 'lib/functions/utils';
import RequestAnalysts from 'pages/Finance/Components/Financing/common/requestAnalysts';
import React, { useEffect, useState } from 'react';

import DocumentsPreview from '../documentsPreview';
import ExportDialog from './components/ExportDialog';

const CustomTooltip = (tableData: any, noHeader?: boolean) => (
  <Table size="small">
    {!noHeader && (
      <TableHead>
        <TableRow>
          <TableCell>Costo</TableCell>
          <TableCell>Valor</TableCell>
        </TableRow>
      </TableHead>
    )}
    <TableBody>
      {tableData.map((row: any) => (
        <TableRow key={row.index}>
          <TableCell>{row.name}</TableCell>
          <TableCell>{row.value}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const styledBox = {
  display: 'flex',
  padding: '6px 0px',
  justifyContent: 'space-between',
  alignContent: 'center',
};

const SimulationDoneFrozen = (props: any) => {
  const { selectedRequest, infoDiv, noInvoices, setSelectedRequest } = props;
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [operationDetails, setOperationDetails] = useState<any>({
    operationAmount: null,
    operationAdvanced: null,
    guaranteeAmount: null,
    operationCostRate: null,
    operationCostIva: null,
    operationCostComissionFixed: null,
    operationCostComissionVariable: null,
    operationToDepost: null,
    expensesCost: null,
    expensesCostIva: null,
    advance: null,
    rate: null,
    guaranteeObservation: null,
  });

  const requestConfiguration = {
    expensesHasIVA: selectedRequest.expensesIVA,
    factoringOperationsSystemFlag:
      selectedRequest.factoringOperationsSystemFlag,
  };

  const updateSimulationValuesPreview = () => {
    const {
      operationAmount,
      operationAdvanced,
      guaranteeAmount,
      operationCostRate,
      operationToDepost,
      operationIva,
      expensesIva,
      operationCostComissionFixed,
      operationCostComissionVariable,
      advance,
      rate,
      expensesCost,
      operationAmountToApply,
      guaranteeObservation,
      arrearType,
      arrearTableId,
      expensesDetails,
    } = calculateRequestDetails(
      selectedRequest,
      requestConfiguration.expensesHasIVA,
      requestConfiguration.factoringOperationsSystemFlag,
    );

    setOperationDetails({
      operationAmount,
      operationAdvanced,
      guaranteeAmount,
      operationCostRate,
      operationToDepost,
      operationIva,
      expensesIva,
      operationCostComissionFixed,
      operationCostComissionVariable,
      advance,
      rate,
      expensesCost,
      operationAmountToApply,
      guaranteeObservation,
      arrearType,
      arrearTableId,
      expensesDetails,
    });
  };

  const operationAmount = operationDetails.operationAmount;
  const operationAdvanced = operationDetails.operationAdvanced;
  const operationCostRate = operationDetails.operationCostRate;
  const operationCostComissionFixed =
    operationDetails.operationCostComissionFixed;
  const operationCostComissionVariable =
    operationDetails.operationCostComissionVariable;
  const operationToDepost = operationDetails.operationToDepost;
  const operationIva = operationDetails.operationIva;
  const expensesIva = operationDetails.expensesIva;
  const operationAmountToApply = operationDetails.operationAmountToApply;
  const expensesCost = operationDetails.expensesCost;
  const advance = operationDetails.advance;
  const rate = operationDetails.rate;
  const guaranteeAmount = operationDetails.guaranteeAmount;
  const amountIVA = expensesIva;

  // All in rate
  const allInCost =
    operationCostRate +
    expensesCost +
    operationCostComissionFixed +
    operationCostComissionVariable +
    operationIva;
  const allInCostRate = ((allInCost / operationAdvanced) * 100).toFixed(2);

  const documents = selectedRequest.documents;
  let finalMonthlyRate = 0;
  for (const document of documents) {
    const realRate = rate / 100;
    const interestRateAdjusted = (realRate * document.daysToCount) / 30;
    const interestCost = document.amountDocument * interestRateAdjusted;

    const fixedProportion =
      (operationCostComissionFixed * document.amountDocument) /
      operationAdvanced;
    const variableProportion =
      (operationCostComissionVariable * document.amountDocument) /
      operationAdvanced;
    const expensesCostProportion =
      (expensesCost * document.amountDocument) / operationAdvanced;
    const operationIvaProportion =
      (operationIva * document.amountDocument) / operationAdvanced;

    const totalCost =
      interestCost +
      fixedProportion +
      variableProportion +
      expensesCostProportion +
      operationIvaProportion;

    const effectiveRate = totalCost / document.amountDocument;
    const monthlyRate = (effectiveRate * 30) / document.daysToCount;

    const monthlyRatePonderated =
      100 * monthlyRate * (document.amountDocument / operationAdvanced);

    finalMonthlyRate += monthlyRatePonderated;
  }

  const handleExportSimulation = async () => {
    setOpenExportDialog(true);
  };

  useEffect(() => {
    updateSimulationValuesPreview();
  }, []);

  const renderAnalysts =
    (selectedRequest.status === 'Aceptada' ||
      selectedRequest.status === 'En giro') &&
    selectedRequest.id;

  return (
    <div>
      <Grid container>
        <Grid item xs={6}>
          <div>
            <div
              style={{
                padding: '0px 0px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography fontWeight="bold" variant="h6">
                Resumen
              </Typography>
            </div>
            {infoDiv}
          </div>
          {renderAnalysts && (
            <RequestAnalysts
              requestId={selectedRequest.id}
              requestType="factoring"
              sx={{ mt: 2, mr: 2.5 }}
            />
          )}
        </Grid>
        <Grid item xs={6}>
          <div style={{ padding: '0px 0px 0px 18px' }}>
            <div
              style={{
                padding: '0px 0px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography fontWeight="bold" variant="h6">
                Detalles
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  onClick={() => handleExportSimulation()}
                  variant="caption"
                  color="primary"
                  style={{ cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Exportar
                </Typography>
              </div>
            </div>
            <div style={styledBox}>
              <Typography variant="body1">Monto operación</Typography>
              <Typography variant="body1">
                {formatMoney(operationAmount)}
              </Typography>
            </div>
            <div style={styledBox}>
              <Typography variant="body1">
                Monto a financiar {`(${advance || 0}%)`}
              </Typography>
              <Typography variant="body1">
                {formatMoney(operationAdvanced)}
              </Typography>
            </div>
            {guaranteeAmount !== 0 && guaranteeAmount !== undefined && (
              <div style={styledBox}>
                <Typography variant="body1">Monto en garantía</Typography>
                <Typography variant="body1">
                  {formatMoney(guaranteeAmount)}
                </Typography>
              </div>
            )}
            <div style={styledBox}>
              <Typography variant="body1">Diferencia precio</Typography>
              <Typography variant="body1">
                {formatMoney(operationCostRate)}
              </Typography>
            </div>
            {operationAmountToApply !== 0 && (
              <div style={styledBox}>
                <Typography variant="body1">Documentos aplicados</Typography>
                <Typography variant="body1">
                  {formatMoney(operationAmountToApply)}
                </Typography>
              </div>
            )}
            {expensesCost !== 0 && (
              <div style={styledBox}>
                <div style={{ display: 'flex' }}>
                  <Typography variant="body1">
                    Otros gastos{amountIVA > 0 ? ' + IVA' : ''}
                  </Typography>
                  <LightTooltip
                    title={CustomTooltip([
                      { name: 'Costo', value: formatMoney(expensesCost) },
                      ...(amountIVA > 0
                        ? [
                            {
                              name: 'IVA',
                              value: formatMoney(expensesIva),
                            },
                          ]
                        : []),
                    ])}
                  >
                    <Info style={{ marginLeft: 6, fontSize: 16 }} />
                  </LightTooltip>
                </div>
                <Typography variant="body1">
                  {formatMoney(expensesCost + amountIVA)}
                </Typography>
              </div>
            )}
            <div style={styledBox}>
              <div style={{ display: 'flex' }}>
                <Typography variant="body1">
                  Comisión operación + IVA
                </Typography>
                <LightTooltip
                  title={CustomTooltip([
                    {
                      name: 'Costo fijo',
                      value: formatMoney(operationCostComissionFixed),
                    },
                    {
                      name: 'Costo variable',
                      value: formatMoney(operationCostComissionVariable),
                    },
                    { name: 'IVA', value: formatMoney(operationIva) },
                    {
                      name: 'Total',
                      value: formatMoney(
                        operationCostComissionFixed +
                          operationCostComissionVariable +
                          operationIva,
                      ),
                    },
                  ])}
                >
                  <Info style={{ marginLeft: 6, fontSize: 16 }} />
                </LightTooltip>
              </div>
              <Typography variant="body1">
                {formatMoney(
                  operationCostComissionFixed +
                    operationCostComissionVariable +
                    operationIva,
                )}
              </Typography>
            </div>
            <div style={{ marginTop: 10 }}>
              <Divider />
              <div style={styledBox}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography fontWeight="bold" variant="body1">
                    Monto a girar
                  </Typography>
                  <LightTooltip
                    title={CustomTooltip(
                      [
                        {
                          name: 'Costo total',
                          value: formatMoney(allInCost),
                        },
                        {
                          name: 'Tasa total (%)',
                          value: `${allInCostRate}%`,
                        },
                        {
                          name: 'Tasa All In (%)',
                          value: `${finalMonthlyRate.toFixed(2)}%`,
                        },
                      ],
                      true,
                    )}
                  >
                    <Info style={{ marginLeft: 6, fontSize: 14 }} />
                  </LightTooltip>
                </div>
                <Typography fontWeight="bold" variant="body1">
                  {formatMoney(operationToDepost)}
                </Typography>
              </div>
            </div>
            {openExportDialog && (
              <ExportDialog
                open={openExportDialog}
                setOpen={setOpenExportDialog}
                selectedRequest={selectedRequest}
                operationDetails={operationDetails}
              />
            )}
          </div>
        </Grid>
      </Grid>

      {!noInvoices && (
        <DocumentsPreview
          selectedRequest={selectedRequest}
          setSelectedRequest={setSelectedRequest}
        />
      )}
    </div>
  );
};

export default SimulationDoneFrozen;
